import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_ALL_ORGANIZATIONS, GET_TRAINING_PACKAGE_AND_TRAINING_BY_ORG_ID,
  GET_ORG_BY_ID,GET_USER_BY_ID} from "../../Graphql/Queries";
import FormItemSelect from "../FormItems/FormItemSelect";
import { Box } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { Error } from "../../Types/MessageType";
import FormItemInput from "../FormItems/FormItemInput";
import TrainingModule from "../InputRequest/TrainingModule";
import { useEffect, useState } from "react";
import { Form } from "antd";
import { useSelector } from 'react-redux'
import Message from "../Message";

type CreateCohortFormProps = {
  alertMessage: Error;
  setTrainingsArray?: (val: string[]) => void;
  currentLocation: boolean;
  onOrgChange: (orgId: string) => void;
  selectedOrgId?: string;
};

const CreateCohortForm = ({
  alertMessage,
  currentLocation,
  setTrainingsArray,
  onOrgChange,
  selectedOrgId,
}: CreateCohortFormProps) => {
  const [form] = Form.useForm();
  const [isRootAdmin, setIsRootAdmin] = useState<boolean>(false);
  const [organizationsOptions, setOrganizationsOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [trainingOptions, setTrainingOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [trainingPackageId, setTrainingPackageId] = useState<string | null>(null);
  const { userId, organization } = useSelector((state: any) => state.auth)

  const { data: userData, loading: userLoading } = useQuery(GET_USER_BY_ID, {
    variables: { userWithRolesByIdId: userId },
    onCompleted(data) {
      const roles = data?.userWithRolesById?.roles || [];
      const isAdmin = roles.some((role: any) => role.roleCode === 'ROOT_ADMIN');
      setIsRootAdmin(isAdmin);
      const orgId = data?.userWithRolesById?.organization || null;
      setTrainingPackageId(orgId);
    },
    onError(err) {
      console.error('Error fetching user data:', err.message);
    },
  });

  const { data: allOrgsData } = useQuery<any>(
    GET_ALL_ORGANIZATIONS,
    {
      variables: {
        page: 1,
        limit: 30,
      },
      skip: !isRootAdmin,
      onCompleted: (data) => {
        const options = data?.allOrganizations?.organizations.map((org: { name: string; _id: string }) => ({
          label: org.name,
          value: org._id,
        })) || [];
        setOrganizationsOptions(options);
      }
    }
  );

  const [getOrgById] = useLazyQuery(GET_ORG_BY_ID, {
    onCompleted: (data) => {
      if (data?.organizationById) {
        const org = data.organizationById;
        setOrganizationsOptions([{
          label: org.name,
          value: org._id
        }]);
        form.setFieldsValue({ organization: org._id });
        handleOrgChange(org._id);
      }
    }
  });

  useEffect(() => {
    if (!isRootAdmin && trainingPackageId) {
      getOrgById({
        variables: {
          organizationByIdId: trainingPackageId
        }
      });
    }
  }, [isRootAdmin, trainingPackageId, getOrgById]);

  useEffect(() => {
    if (currentLocation) {
      const orgId = form.getFieldValue("organization");
      if (orgId) {
        form.setFieldsValue({ organization: orgId });
      }
    }
  }, [currentLocation, form]);

  // Fetch trainings based on selected organization
  const [fetchTrainingsByOrgId, { loading: trainingLoading }] = useLazyQuery(
    GET_TRAINING_PACKAGE_AND_TRAINING_BY_ORG_ID,
    {
      onCompleted(data) {
        const trainingPackages = data?.getTrainingPackageAndTrainingByOrgId?.trainingPackages || [];
        const trainings = trainingPackages.flatMap((pkg: any) =>
          pkg.trainings.map((training: any) => ({
            label: training.name,
            value: training._id,
          }))
        );
        setTrainingOptions(trainings);
      },
      onError(err) {
        console.error("Error fetching trainings:", err.message);
      },
    }
  );
  

  useEffect(() => {
    if (selectedOrgId && trainingOptions.length === 0) {
      fetchTrainingsByOrgId({
        variables: { getTrainingPackageAndTrainingByOrgIdId: selectedOrgId },
      });
    }
  }, [selectedOrgId]);
  

  const handleOrgChange = (value: string) => {
    onOrgChange(value);
    fetchTrainingsByOrgId({ variables: { getTrainingPackageAndTrainingByOrgIdId: value } });
  };

  useEffect(() => {
    const orgId = form.getFieldValue("organization");
    if (orgId && trainingOptions.length === 0) {
      fetchTrainingsByOrgId({ variables: { getTrainingPackageAndTrainingByOrgIdId: orgId } });
    }
  }, []);   

  return (
    <>
      <Grid2 container spacing={2} paddingX={3}>
        <Grid2 xs={12} sm={6}>
          <FormItemInput label="Batch Name" name="cohortName" rules={[{ required: true, message: 'Cohort name is required' }]} type={'text'} />
        </Grid2>
        <Grid2 xs={12} sm={6}>
          <FormItemInput label="Batch Start Date" name="cohortStartDate" type={'date'} />
        </Grid2>
        <Grid2 xs={12} sm={6}>
          <FormItemInput 
            label="Batch Description" 
            name="batchDescription" 
            rules={[{ required: true, message: 'Batch description is required' }]} 
            type={'text'} 
          />
        </Grid2>
        {isRootAdmin && (
          <Grid2 xs={12} sm={6}>
            <FormItemSelect
              label="Organization"
              name="organization"
              rules={[{ required: true, message: "Organization is required" }]}
              value={organizationsOptions}
              setValue={(v) => {
                form.setFieldsValue({ organization: v });
                handleOrgChange(v);
              }}
            />
          </Grid2>
        )}
        <Grid2 xs={12} sm={6}>
          <FormItemSelect
            label="Trainings"
            name="cohortTrainings"
            rules={[{ required: true, message: "Trainings are required" }]}
            mode="multiple"
            value={trainingOptions}
            setValue={(v) => {
              if (setTrainingsArray) {
                setTrainingsArray([v]);
              }
            }}
          />
        </Grid2>
        <Grid2 xs={12} sm={6}>
          <FormItemInput label="Batch Head Name" name="cohortHeadName"  type={'text'} />
        </Grid2>
        <Grid2 xs={12} sm={6}>
          <FormItemInput label="Batch Head Email" name="cohortHeadEmail"  type={'text'} />
        </Grid2>
      </Grid2>
      {alertMessage?.message && (
        <Box mt={2}>
          <Message type={alertMessage.type} message={alertMessage.message} variant="filled" />
        </Box>
      )}
    </>
  );
};

export default CreateCohortForm;